import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "./lib/backend";

const AuthContext = createContext(null);
const API = `${BACKEND_URL}/api`;

// HttpOnly auth + CSRF cookies are set by the backend on /auth/verify and
// /auth/claim-admin. The browser sends them automatically as long as
// `withCredentials: true` is set on every axios call — we make that the
// default globally so existing call sites don't have to be touched.
axios.defaults.withCredentials = true;

// Same-origin setups (preview) can read the ti_csrf_token cookie directly —
// axios then mirrors it into the X-CSRF-Token header automatically.
axios.defaults.xsrfCookieName = "ti_csrf_token";
axios.defaults.xsrfHeaderName = "X-CSRF-Token";
axios.defaults.withXSRFToken = true;

// ─── CSRF token plumbing ──────────────────────────────────────────────────────
// In production the frontend (tourismintel.ai) and the API live on DIFFERENT
// domains, so document.cookie can never read the CSRF cookie set by the API.
// The backend therefore echoes the token in the login JSON body (and exposes
// GET /auth/csrf for re-sync); we persist it in localStorage and attach it to
// every mutating request via a global request interceptor.
const CSRF_STORAGE_KEY = "ti_csrf_token";

const readCsrfCookie = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)ti_csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const readStoredCsrf = () => {
  try { return localStorage.getItem(CSRF_STORAGE_KEY) || ""; } catch { return ""; }
};

const storeCsrf = (token) => {
  try {
    if (token) localStorage.setItem(CSRF_STORAGE_KEY, token);
    else localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch { /* storage unavailable — cookie fallback still works same-origin */ }
};

// Cookie wins when readable (same-origin): it is always the value the server
// compares against. localStorage is the cross-origin fallback.
export const getCsrfValue = () => readCsrfCookie() || readStoredCsrf();

// Global interceptor: attach X-CSRF-Token to every mutating request that
// doesn't already carry one (covers call sites that forgot authHeader()).
const CSRF_SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (CSRF_SAFE_METHODS.has(method)) return config;
  const headers = config.headers || {};
  const existing = typeof headers.get === "function"
    ? headers.get("X-CSRF-Token")
    : headers["X-CSRF-Token"] || headers["x-csrf-token"];
  if (!existing) {
    const csrf = getCsrfValue();
    if (csrf) {
      if (typeof headers.set === "function") headers.set("X-CSRF-Token", csrf);
      else config.headers = { ...headers, "X-CSRF-Token": csrf };
    }
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Connection health surfaced to the UI:
  //   "healthy"      → all good
  //   "reconnecting" → at least one in-flight request is mid-retry (5xx/520)
  //   "down"         → exhausted retries, request actually failed
  const [connectionState, setConnectionState] = useState("healthy");
  const navigate = useNavigate();
  const location = useLocation();

  // Ref-mirror of connectionState so the axios interceptor (closure over the
  // INITIAL setter) can read the current value without re-binding on every
  // state change. Avoids the "stale state in interceptor" footgun.
  const connectionStateRef = useRef("healthy");
  // Counter of in-flight retry attempts. The banner shows "reconnecting" as
  // long as this is > 0, regardless of whether OTHER unrelated requests are
  // succeeding (otherwise a single concurrent 200 would prematurely clear it).
  const pendingRetriesRef = useRef(0);

  const setConnection = useCallback((next) => {
    if (connectionStateRef.current === next) return;
    connectionStateRef.current = next;
    setConnectionState(next);
  }, []);

  // Decide what the banner should display based on the current pendingRetries
  // count + the last terminal outcome. Called from the interceptor success/
  // failure paths to ensure transitions are race-free.
  const reconcileConnection = useCallback((lastTerminal /* "ok" | "fail" | null */) => {
    if (pendingRetriesRef.current > 0) {
      setConnection("reconnecting");
      return;
    }
    if (lastTerminal === "fail") {
      setConnection("down");
      return;
    }
    setConnection("healthy");
  }, [setConnection]);

  const loadUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      // Cross-origin self-heal: sessions that predate the localStorage CSRF
      // fix have a valid auth cookie but no readable CSRF token — re-sync it
      // from the API so writes don't 403.
      if (!getCsrfValue()) {
        try {
          const c = await axios.get(`${API}/auth/csrf`);
          storeCsrf(c.data?.csrf_token);
        } catch { /* non-fatal — user can re-login */ }
      }
    } catch {
      // Functional update: never clobber a user that login() set while this
      // initial /auth/me probe was still in flight (magic-link verify race).
      setUser(prev => prev ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Global 401 handler: when an API call returns 401 while we believed the
  // user was authenticated, verify the session is *actually* dead by re-probing
  // /auth/me. Only if that ALSO 401s do we log out + bounce to /auth.
  //
  // Why the extra probe: in production we've seen one-off 401s on a single
  // admin endpoint (cross-origin cookie races, Cloudflare edge inconsistencies,
  // backend role-check edge cases) while the session itself is still valid.
  // Without this guard a single transient 401 silently kicks the user out and
  // shows them the login screen — which is what the "ogni volta che vado su
  // admin mi butta fuori" bug was. Verifying the session avoids the false
  // positive while still catching real expirations.
  //
  // PRODUCTION HARDENING (Jun 28): if the verify probe ALSO 401s, we wait
  // 500ms and probe ONE more time before kicking the user out. This handles
  // the Cloudflare edge race where the API edge sees stale cookie metadata
  // for ~1 RTT after a navigation. A genuinely-dead session will still 401
  // on both probes; an alive-but-edge-flaky session will recover on the 2nd.
  useEffect(() => {
    // Debounce: a burst of admin-tab requests can all 401 at once. Only run
    // the verification probe once per burst.
    let verifyInFlight = null;

    const verifyTwice = async () => {
      try {
        await axios.get(`${API}/auth/me`);
        return true; // alive
      } catch (err1) {
        if (err1?.response?.status !== 401) {
          // Network/5xx — keep the user logged in; let original error bubble.
          return true;
        }
        // First probe 401d. Wait a beat and try again to rule out an edge
        // race.  console-log so prod issues can be diagnosed by the user
        // sharing their browser console output.
        try { console.warn("[auth] /auth/me 401'd — retrying in 500ms before kicking out", { cookies: typeof document !== "undefined" ? document.cookie : "n/a" }); } catch {}
        await new Promise((r) => setTimeout(r, 500));
        try {
          await axios.get(`${API}/auth/me`);
          try { console.info("[auth] /auth/me recovered on retry — keeping session"); } catch {}
          return true; // alive after retry
        } catch (err2) {
          if (err2?.response?.status === 401) {
            try { console.warn("[auth] /auth/me 401'd twice — session truly expired, kicking out"); } catch {}
            return false; // truly dead
          }
          return true; // non-401 on retry → keep alive
        }
      }
    };

    const interceptor = axios.interceptors.response.use(
      (resp) => {
        // Successful response. Only mark healthy if there are no pending
        // retries (otherwise a concurrent success would prematurely clear
        // the "reconnecting" banner while another request is still mid-retry).
        if (pendingRetriesRef.current === 0) {
          setConnection("healthy");
        }
        return resp;
      },
      async (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const config = error?.config || {};

        // ── Cloudflare backend errors (520-524) ────────────────────────────
        // 520 = origin not responding, 521 = down, 522 = timeout, 523 = origin
        // unreachable, 524 = origin took >100s. These are TRANSIENT backend
        // problems, NEVER auth failures.
        const isCloudflareBackendErr = status >= 520 && status <= 524;

        // ── Silent retry on transient 5xx / Cloudflare errors ──────────────
        // Up to 3 attempts with exponential backoff (500ms, 1s, 2s).
        // Opt-out via `config.__noRetry = true` on the original request.
        const isRetryable = (
          status === undefined           // network error (no response)
          || (status >= 500 && status <= 599)
        ) && !config.__noRetry
          && (config.method || "get").toUpperCase() === "GET"; // only idempotent GETs

        if (isRetryable) {
          config.__retryCount = config.__retryCount || 0;
          if (config.__retryCount < 3) {
            config.__retryCount += 1;
            pendingRetriesRef.current += 1;
            reconcileConnection(null);
            const delay = 500 * Math.pow(2, config.__retryCount - 1); // 500, 1000, 2000
            try {
              console.warn(`[connection] ${isCloudflareBackendErr ? "Cloudflare " + status : "5xx"} on ${url} — retry ${config.__retryCount}/3 in ${delay}ms`);
            } catch { /* noop */ }
            await new Promise((r) => setTimeout(r, delay));
            try {
              const resp = await axios.request(config);
              pendingRetriesRef.current = Math.max(0, pendingRetriesRef.current - 1);
              reconcileConnection("ok");
              try { console.info(`[connection] recovered on retry ${config.__retryCount}/3`); } catch { /* noop */ }
              return resp;
            } catch (retryErr) {
              pendingRetriesRef.current = Math.max(0, pendingRetriesRef.current - 1);
              // Don't reconcile here — the nested interceptor call already did,
              // OR will reach the exhausted-retries branch below in the parent.
              error = retryErr;
            }
          } else {
            // Exhausted retries — backend genuinely unavailable.
            reconcileConnection("fail");
          }
        }

        // ── Existing 401 → verify-and-logout logic (unchanged) ─────────────
        const newStatus = error?.response?.status;
        const isAuthProbe = /\/auth\/(me|verify|claim-admin|login|logout|magic|mobile-magic|csrf)$/.test(url);
        const onAuthPage = location.pathname.startsWith("/auth");
        if (newStatus === 401 && user && !isAuthProbe && !onAuthPage) {
          try { console.warn("[auth] received 401 from", url, "— probing /auth/me"); } catch {}
          if (!verifyInFlight) {
            verifyInFlight = verifyTwice().finally(() => {
              setTimeout(() => { verifyInFlight = null; }, 1000);
            });
          }
          const alive = await verifyInFlight;
          if (!alive) {
            setUser(null);
            navigate("/auth", { replace: true, state: { reason: "session_expired" } });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [user, navigate, location.pathname, setConnection, reconcileConnection]);

  // login() is invoked after /auth/verify or /auth/claim-admin succeed — the
  // server has already set the HttpOnly cookie; we hydrate state and persist
  // the CSRF token echoed in the JSON body (cross-origin can't read the cookie).
  const login = (_token, userData, csrfToken) => {
    if (csrfToken) storeCsrf(csrfToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      // The request interceptor attaches the X-CSRF-Token header.
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      /* server-side cookie clear is best-effort */
    }
    storeCsrf("");
    setUser(null);
  };

  // Returns the header(s) that must be attached to authenticated requests.
  // With cookie auth, the JWT rides in the HttpOnly cookie automatically; the
  // only thing we still need to set explicitly is the CSRF token for write
  // operations. We always include it — the backend ignores it on safe (GET)
  // requests and on Bearer-authenticated requests.
  const authHeader = useCallback(() => {
    const csrf = getCsrfValue();
    return csrf ? { "X-CSRF-Token": csrf } : {};
  }, []);

  const getCsrfToken = useCallback(() => getCsrfValue(), []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, authHeader, getCsrfToken, isAdmin: user?.is_admin, connectionState }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
